import { useEffect } from 'react';

import type { SelectionListIdNames, TreeItem } from '@revelio/iso-utility';

import { useAllCustomSkillLevels } from '../../../../../shared/selection-lists/hooks/useAllCustomSkillLevels';
import { useSpecificCustomSkills } from '../../../../../shared/selection-lists/hooks/useSpecificCustomSkills';
import type { EntitySelections } from '../../report-builder.model';
import {
  findColumnWithId,
  isColumnId,
  updateReportEntitySelections,
} from './report-entity-selection.utils';
import { filterToSupportedLevels } from './report-entity-selections/custom-skills.helpers';
import { HIDDEN_REPORT_FIELDS } from './report-hidden-columns';
import type { ReportEntityConfig } from './utils';

const createSkillTaxonomyTreeItem = (
  keyword:
    | typeof HIDDEN_REPORT_FIELDS.SKILL_TAXONOMY_VERSION
    | typeof HIDDEN_REPORT_FIELDS.SKILL_TAXONOMY_NAME,
  value: string
): TreeItem<string> => {
  return {
    id: value,
    parentId: undefined,
    selectionListId: keyword as unknown as SelectionListIdNames,
    item: {
      id: value,
      label: value,
      shortName: value,
    },
    children: [],
  };
};

/**
 * Injects hidden skill taxonomy metadata fields if requested by the report config:
 * - skill_taxonomy_version
 * - skill_taxonomy_name
 *
 * Derives taxonomy metadata from the custom skill selection lists, which include
 * tax_id, tax_name, and tax_version in the API response.
 */
export const useReportsSkillTaxonomy = (
  entityConfigs: ReportEntityConfig | null,
  selectedReportId?: number,
  _entitySelections?: EntitySelections
) => {
  const versionColumn = findColumnWithId(
    entityConfigs,
    HIDDEN_REPORT_FIELDS.SKILL_TAXONOMY_VERSION
  );
  const nameColumn = findColumnWithId(
    entityConfigs,
    HIDDEN_REPORT_FIELDS.SKILL_TAXONOMY_NAME
  );

  // Fetch supported custom skill levels to build the minimal query for metadata
  const { levels } = useAllCustomSkillLevels({
    pause: !entityConfigs || (!versionColumn && !nameColumn),
  });

  // taxonomyId 0 means "default/latest" taxonomy in our selection list API
  const { data } = useSpecificCustomSkills({
    levelIds: filterToSupportedLevels(levels).map((l) => l.level_id),
    taxonomyId: 0,
  });

  const taxonomyName = data?.[0]?.tax_name;
  const taxonomyVersion = data?.[0]?.tax_version;

  useEffect(() => {
    if (
      !entityConfigs ||
      selectedReportId === undefined ||
      selectedReportId === null
    ) {
      return;
    }

    // Only proceed if at least one metadata piece exists
    if (!taxonomyName && !taxonomyVersion) return;

    // Inject version if requested
    if (taxonomyVersion) {
      const versionColumn = entityConfigs.columns?.find((c) =>
        isColumnId(HIDDEN_REPORT_FIELDS.SKILL_TAXONOMY_VERSION, c)
      );
      if (versionColumn) {
        updateReportEntitySelections({
          reportId: selectedReportId,
          reportEntity: versionColumn.id,
          selections: {
            [HIDDEN_REPORT_FIELDS.SKILL_TAXONOMY_VERSION]:
              createSkillTaxonomyTreeItem(
                HIDDEN_REPORT_FIELDS.SKILL_TAXONOMY_VERSION,
                taxonomyVersion
              ),
          },
        });
      }
    }

    // Inject name if requested
    if (taxonomyName) {
      const nameColumn = entityConfigs.columns?.find((c) =>
        isColumnId(HIDDEN_REPORT_FIELDS.SKILL_TAXONOMY_NAME, c)
      );
      if (nameColumn) {
        updateReportEntitySelections({
          reportId: selectedReportId,
          reportEntity: nameColumn.id,
          selections: {
            [HIDDEN_REPORT_FIELDS.SKILL_TAXONOMY_NAME]:
              createSkillTaxonomyTreeItem(
                HIDDEN_REPORT_FIELDS.SKILL_TAXONOMY_NAME,
                taxonomyName
              ),
          },
        });
      }
    }
  }, [entityConfigs, selectedReportId, taxonomyName, taxonomyVersion]);
};
